import {
  useState,
  useEffect,
  useMemo,
  Suspense,
  Component as ReactComponent,
  type ComponentType,
  type ReactNode,
  type ErrorInfo,
} from "react";
import { useSearchParams, Link } from "react-router-dom";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";

// ─── Dynamic module registry via Vite glob ───────────────

/**
 * Eagerly glob all workshop .tsx files as lazy loaders.
 * Each key is like "./components/canvas/floating-toolbar.tsx"
 * and the value is a () => Promise<module>.
 */
const workshopModules = import.meta.glob<Record<string, unknown>>(
  ["../**/*.tsx"],
  { eager: false },
);

/**
 * Build a lookup from manifest-style paths
 * (e.g. "workshop/components/canvas/floating-toolbar.tsx")
 * to the glob key (e.g. "./components/canvas/floating-toolbar.tsx").
 */
const MODULE_MAP: Record<string, () => Promise<Record<string, unknown>>> = {};

for (const [globKey, loader] of Object.entries(workshopModules)) {
  // globKey is relative to this file's directory (../), so:
  // "../components/canvas/floating-toolbar.tsx" → strip "../"
  const relative = globKey.replace("../", "");
  // manifest uses "workshop/..." prefix
  const manifestKey = `workshop/${relative}`;
  MODULE_MAP[manifestKey] = loader as () => Promise<Record<string, unknown>>;
}

// ─── Error boundary ──────────────────────────────────────

function ErrorFallback({ error, filePath }: { error: Error; filePath: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/10">
        <Icons.XCircle className="h-6 w-6 text-red-400" />
      </div>
      <div>
        <h2 className="text-sm font-semibold text-white/80">
          Failed to render component
        </h2>
        <p className="mt-1 text-xs text-white/40">
          This component likely requires props that can&apos;t be inferred automatically.
        </p>
        <code className="mt-3 block rounded-lg bg-white/5 px-4 py-2 text-xs text-red-300/70">
          {error.message}
        </code>
        <p className="mt-3 text-xs text-white/30">
          File: <code className="text-white/40">{filePath}</code>
        </p>
      </div>
    </div>
  );
}

// ─── Lazy component wrapper ──────────────────────────────

function LazyPreview({ filePath }: { filePath: string }) {
  const [mod, setMod] = useState<Record<string, unknown> | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [renderError, setRenderError] = useState<Error | null>(null);

  useEffect(() => {
    const loader = MODULE_MAP[filePath];
    if (!loader) {
      setLoadError(new Error(`Module not found in workshop: ${filePath}`));
      return;
    }

    loader()
      .then((m) => setMod(m))
      .catch((err) =>
        setLoadError(err instanceof Error ? err : new Error(String(err))),
      );
  }, [filePath]);

  // Find the first exported React component (function returning JSX)
  const Component = useMemo(() => {
    if (!mod) return null;

    // Prefer named exports that look like components (PascalCase functions)
    for (const [key, val] of Object.entries(mod)) {
      if (
        key === "default" ||
        typeof val !== "function" ||
        /^[a-z]/.test(key)
      ) {
        continue;
      }
      return val as ComponentType;
    }

    // Fall back to default export
    if (typeof mod.default === "function") {
      return mod.default as ComponentType;
    }

    return null;
  }, [mod]);

  if (loadError) {
    return <ErrorFallback error={loadError} filePath={filePath} />;
  }

  if (!mod) {
    return (
      <div className="flex items-center justify-center p-12">
        <Icons.Spinner className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  if (!Component) {
    return (
      <ErrorFallback
        error={new Error("No React component found in module exports")}
        filePath={filePath}
      />
    );
  }

  if (renderError) {
    return <ErrorFallback error={renderError} filePath={filePath} />;
  }

  // Render — catch errors via error boundary pattern
  try {
    return (
      <ErrorCatcher onError={setRenderError}>
        <Suspense
          fallback={
            <div className="flex items-center justify-center p-12">
              <Icons.Spinner className="h-5 w-5 animate-spin text-white/30" />
            </div>
          }
        >
          <Component />
        </Suspense>
      </ErrorCatcher>
    );
  } catch (err) {
    return (
      <ErrorFallback
        error={err instanceof Error ? err : new Error(String(err))}
        filePath={filePath}
      />
    );
  }
}

// ─── Error boundary (class component) ────────────────────

interface ErrorCatcherProps {
  children: ReactNode;
  onError: (error: Error) => void;
}

interface ErrorCatcherState {
  error: Error | null;
}

class ErrorCatcher extends ReactComponent<ErrorCatcherProps, ErrorCatcherState> {
  constructor(props: ErrorCatcherProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorCatcherState {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    this.props.onError(error);
  }

  render() {
    if (this.state.error) {
      return null; // Parent handles the error display
    }
    return this.props.children;
  }
}

// ─── Export list ─────────────────────────────────────────

function ExportList({ filePath }: { filePath: string }) {
  const [exports, setExports] = useState<string[]>([]);

  useEffect(() => {
    const loader = MODULE_MAP[filePath];
    if (!loader) return;

    loader().then((m) => {
      setExports(Object.keys(m).filter((k) => typeof m[k] === "function"));
    });
  }, [filePath]);

  if (exports.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {exports.map((name) => (
        <span
          key={name}
          className="rounded-md bg-white/5 px-2 py-0.5 text-xs font-mono text-white/50"
        >
          {name}
        </span>
      ))}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────

export function ComponentPreviewPage() {
  const [searchParams] = useSearchParams();
  const filePath = searchParams.get("file");

  if (!filePath) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/5">
            <Icons.Eye className="h-6 w-6 text-white/30" />
          </div>
          <h1 className="text-lg font-semibold text-white/80">
            Component Preview
          </h1>
          <p className="mt-1 text-sm text-white/40">
            Select a component from the Sync Status page to preview it here.
          </p>
          <Link
            to="/workshop/sync-status"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent hover:text-accent/80"
            data-testid="preview-back-link"
          >
            <Icons.ArrowLeft className="h-3.5 w-3.5" />
            Go to Sync Status
          </Link>
        </div>
      </div>
    );
  }

  const fileName = filePath.split("/").pop() ?? filePath;
  const dirPath = filePath.replace(`/${fileName}`, "").replace("workshop/", "");

  return (
    <div
      className="flex h-full flex-col"
      data-testid="component-preview-page"
    >
      {/* Header bar */}
      <div className="shrink-0 border-b border-white/[0.06] bg-white/[0.01] px-5 py-3">
        <div className="flex items-center gap-3">
          <Link
            to="/workshop/sync-status"
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-md",
              "text-white/40 transition-colors hover:bg-white/5 hover:text-white/70",
            )}
            data-testid="preview-back-btn"
          >
            <Icons.ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <h1 className="text-sm font-semibold text-white/90">{fileName}</h1>
              <span className="truncate text-xs text-white/30">{dirPath}</span>
            </div>
            <div className="mt-1">
              <ExportList filePath={filePath} />
            </div>
          </div>
        </div>
      </div>

      {/* Preview area */}
      <div className="relative flex-1 overflow-auto">
        <LazyPreview filePath={filePath} />
      </div>
    </div>
  );
}
